import React from "react";
import styled from "styled-components";
import { colors } from "../../utils/theme";
import RevealOnScroll from "../../utils/RevealOnScroll";
import WorkButtons from "../Header/WorkButtons";
import { SiAdobepremierepro, SiAdobeaftereffects, SiInstagram, SiCanva, SiAdobecreativecloud } from "react-icons/si";
import { motion } from "framer-motion";
import TextShift from "../../utils/TextShift";
import AnimatedProjectCard from "./AnimatedProjectCard";
import placeholderImage from '../../images/placeholder.png';
import arcteryxLogo from '../../images/FeaturedProjects/Arcteryx/Arcteryx.png';
import redbullLogo from '../../images/FeaturedProjects/Redbull/RedBull.png';
import csscLogo from '../../images/FeaturedProjects/CSSC/CSSC.png';

const EngageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  color: white;
  background: transparent;
  padding: 0 1rem 4rem;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 0.5rem 3rem;
  }
`;

const TitleContainer = styled.div`
  margin-top: 6rem;
  margin-bottom: 0;
  opacity: 0.9;
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding-bottom: 1.3rem;
  }
`;

const NavigationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  margin-top: -2rem;
`;

const SubtextContainer = styled.div`
  width: 100%;
  max-width: 600px;
  text-align: left;
  padding: 2rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 1rem;
  }
`;

const Subtext = styled.p`
  font-family: "maitree", serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #9f9f9f;
  font-weight: 300;
  max-width: 600px;
  margin: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.5;
    padding: 0 1rem;
    max-width: 100%;
  }
`;

const TechStackContainer = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 1rem 2rem 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6.4rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem 0rem 0;
    gap: 3rem;
    justify-content: center;
    flex-wrap: wrap;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 3rem;
    padding: 1rem 0 0;
  }
`;

const IconWrapper = styled(motion.div)`
  color: #9f9f9f;
  font-size: 2.4rem;
  opacity: 0.7;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  &:hover {
    color: white;
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const iconVariants = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
};

const ProjectsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 4rem;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 2rem;
    align-items: center;
  }
`;

const ProjectSection = styled.div`
  display: flex;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : 'flex-end'};
  width: 100%;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const Engage = () => {
  return (
    <EngageContainer>
      <RevealOnScroll>
        <TitleContainer>
          <TextShift
            text="Engage"
            fontSize="10rem"
            mobileFontSize="5rem"
            fontFamily="moret-variable, serif"
            maxWeight={900}
            minWeight={200}
            adjacentWeightChange={100}
          />
        </TitleContainer>
      </RevealOnScroll>

      <NavigationContainer>
        <WorkButtons excludeButton={"engage"} />
      </NavigationContainer>

      <SubtextContainer>
        <RevealOnScroll>
          <Subtext>
          I create content that connects, driving engagement, building community, and strengthening brands. From sponsorships to full-scale campaigns, content that resonates and delivers real interaction.
          </Subtext>
        </RevealOnScroll>
      </SubtextContainer>

      <RevealOnScroll>
        <TechStackContainer>
          <IconWrapper
            variants={iconVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.1 }}
          >
            <SiAdobepremierepro />
          </IconWrapper>
          <IconWrapper
            variants={iconVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.2 }}
          >
            <SiAdobeaftereffects />
          </IconWrapper>
          <IconWrapper
            variants={iconVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.3 }}
          >
            <SiInstagram />
          </IconWrapper>
          <IconWrapper
            variants={iconVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.4 }}
          >
            <SiCanva />
          </IconWrapper>
          <IconWrapper
            variants={iconVariants}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.5 }}
          >
            <SiAdobecreativecloud />
          </IconWrapper>
        </TechStackContainer>
      </RevealOnScroll>

      <ProjectsContainer>
        <RevealOnScroll>
          <ProjectSection align="left">
            <AnimatedProjectCard
              title="CSSC"
              subtitle="Building community, creating content, and bringing excitement to every ride."
              image={csscLogo}
              backgroundColor="#1A1A1A"
              to="/projects/cssc"
            />
          </ProjectSection>
        </RevealOnScroll>

        <RevealOnScroll>
          <ProjectSection align="right">
            <AnimatedProjectCard
              title="Arc'teryx"
              subtitle="Elevating the ride with premium gear and community-driven content."
              image={arcteryxLogo}
              backgroundColor="#1A1A1A"
              to="/projects/arcteryx"
            />
          </ProjectSection>
        </RevealOnScroll>

        <RevealOnScroll>
          <ProjectSection align="left">
            <AnimatedProjectCard
              title="Redbull"
              subtitle="Fueling the stoke with high-energy events and next-level content."
              image={redbullLogo}
              backgroundColor="#1A1A1A"
              to="/projects/redbull"
            />
          </ProjectSection>
        </RevealOnScroll>
      </ProjectsContainer>
    </EngageContainer>
  );
};

export default Engage;
